import { NgTemplateOutlet } from '@angular/common';
import {
    Component,
    computed,
    contentChild,
    DestroyRef,
    effect,
    inject,
    input,
    output,
    type Signal,
    signal,
    type TemplateRef,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import type { FormGroup } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { ConfirmationService, type MenuItem } from '@openng/optimus-ui/api';
import { ButtonModule } from '@openng/optimus-ui/button';
import { ButtonGroupModule } from '@openng/optimus-ui/buttongroup';
import { DialogModule } from '@openng/optimus-ui/dialog';
import { PopoverModule } from '@openng/optimus-ui/popover';
import { SplitButtonModule } from '@openng/optimus-ui/splitbutton';
import { TooltipModule } from '@openng/optimus-ui/tooltip';
import { injectParams } from 'ngxtension/inject-params';
import { filter, map, startWith } from 'rxjs';

import { ORGANIZATION_PERMISSION } from '@app/auth/models/user.model';
import { AuthenticationService } from '@app/auth/services/authentication.service';
import type { FunctionInput } from '@app/modules/assets/models/global-function.model';
import type { PIPELINE_TYPE } from '@app/modules/assets/models/pipeline-builder.model';
import { DownloadOptionsComponent } from '@app/shared/components/download-options/download-options.component';
import { EditChangeLogComponent } from '@app/shared/components/edit-change-log/edit-change-log.component';
import { ExecutionOptionsComponent } from '@app/shared/components/execution-options/execution-options.component';
import { MoveProjectComponent } from '@app/shared/components/move-project/move-project.component';
import { ProjectAccessRequestDialogComponent } from '@app/shared/components/project-access-request-dialog/project-access-request-dialog.component';
import { DeleteObjectComponent } from '@app/shared/components/registry/delete-object/delete-object.component';
import { RegistryService } from '@app/shared/components/registry/registry.service';
import {
    ACTIONS,
    OBJECTS,
    SIMULATABLE_OBJECTS,
    WORKFLOW_STATUS,
    WORKFLOWABLE_OBJECTS,
} from '@app/shared/constants/shared.constant';
import type { ItemAction } from '@app/shared/interface/common.interface';
import type { NgActivatedRoute } from '@app/shared/interface/ng-router.interface';
import { AppConfigService } from '@app/shared/services/app-config.service';
import { KeyboardShortcutService } from '@app/shared/services/keyboard-shortcut.service';
import type { WithCrudService } from '@app/shared/services/mixins/crud.service';
import type { WithExportableService } from '@app/shared/services/mixins/export.service';
import type { WithJobService } from '@app/shared/services/mixins/job.service';
import type { WithLogService } from '@app/shared/services/mixins/log.service';
import type { WithCrudVersionService } from '@app/shared/services/mixins/version.service';
import type { WithWorkflowableService } from '@app/shared/services/mixins/workflow.service';
import { PermissionService } from '@app/shared/services/permission.service';
import { ToastNotificationService } from '@app/shared/services/toast-notification.service';
import { FormHelper } from '@app/shared/utils/form-helper';
import { Helpers } from '@app/shared/utils/helpers';
import { TAB_ROUTES } from '@app/shared/utils/route-configs';
import type { RegistryRouteData } from '@app/shared/utils/router-factory';
import { RouterHelper } from '@app/shared/utils/router-helper';

// FIXME: Ideally all simulatable objects should be exportable but backend doesnt support for quality profile
const EXPORTABLE_OBJECTS = [
    OBJECTS.GLOBAL_FUNCTION,
    OBJECTS.PROMPT,
    OBJECTS.FOUNDATION_MODEL,
    OBJECTS.PIPELINE,
    OBJECTS.RAG,
    OBJECTS.REPORT,
];

export const COPYABLE_OBJECTS = [
    OBJECTS.DATA_TABLE,
    OBJECTS.GLOBAL_FUNCTION,
    OBJECTS.PROJECT_ROLE,
    OBJECTS.APPROVAL_WORKFLOW,
    OBJECTS.REPORT,
    OBJECTS.PROMPT,
    OBJECTS.FOUNDATION_MODEL,
    OBJECTS.RAG,
    OBJECTS.PIPELINE,
];

const NON_DELETABLE_OBJECTS = [OBJECTS.USER, OBJECTS.PLATFORM_SETTING];

enum SAVE_TYPES {
    WITH_COMMENT = 'with_comment',
    WITHOUT_COMMENT = 'without_comment',
}

export interface Item {
    id: number;
    name: string;
    alias?: string;
    createdBy?: string;
    actions?: ItemAction;
    parentId?: number;
    version?: number;
    currentStatus?: WORKFLOW_STATUS;
    projectSlug?: string;

    arguments?: FunctionInput[]; // Used to display arguments in 'Export Python Code' for GenAI objects and GF
    pipelineType?: PIPELINE_TYPE; // Used to display arguments in 'Export Python Code' for ChatBased pipeline
}

const NON_EDITABLE_TABS = [
    TAB_ROUTES.DASHBOARD,
    TAB_ROUTES.JOBS,
    TAB_ROUTES.APPROVALS,
    TAB_ROUTES.CHANGE_HISTORY,
    TAB_ROUTES.OVERVIEW,
    TAB_ROUTES.CHAT_SESSION,
    TAB_ROUTES.INTEGRATIONS,
];

@Component({
    selector: 'cor-action-btns',
    templateUrl: './action-btns.component.html',
    styleUrl: './action-btns.component.scss',
    imports: [
        ExecutionOptionsComponent,
        EditChangeLogComponent,
        DownloadOptionsComponent,
        DeleteObjectComponent,
        DialogModule,
        TooltipModule,
        RouterModule,
        ButtonModule,
        PopoverModule,
        NgTemplateOutlet,
        ProjectAccessRequestDialogComponent,
        MoveProjectComponent,
        SplitButtonModule,
        ButtonGroupModule,
    ],
})
export class ActionBtnsComponent {
    protected readonly currentProject = injectParams<string>('projectSlug');

    readonly id = input<number>();
    readonly versionList = input<Item[]>([]);
    readonly item = input.required<Item>();
    protected readonly object = input.required<OBJECTS>();
    protected readonly service = input.required<
        WithCrudService<Item> &
            Partial<WithJobService> &
            Partial<WithExportableService> &
            Partial<WithLogService> &
            Partial<WithCrudVersionService<Item>> &
            Partial<WithWorkflowableService>
    >();

    readonly extraOptions = contentChild<TemplateRef<any>>('extraOptions');

    editEvent = output<void>();
    stopEditingEvent = output<void>();
    importEvent = output<void>();

    protected ACTIONS = ACTIONS;
    protected WORKFLOW_STATUS = WORKFLOW_STATUS;

    readonly showNoAccessDialog = signal(false);

    private readonly permissionService = inject(PermissionService);
    private readonly authService = inject(AuthenticationService);
    protected readonly appConfigService = inject(AppConfigService);
    protected readonly registryService = inject(RegistryService);
    private readonly destroyRef = inject(DestroyRef);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute) as NgActivatedRoute<
        RegistryRouteData,
        { id: number; transactionId: number }
    >;
    private readonly confirmationService = inject(ConfirmationService);
    private readonly toast = inject(ToastNotificationService);
    private readonly keyboardShortcutService = inject(KeyboardShortcutService);

    protected readonly SAVE_TYPES = SAVE_TYPES;
    protected readonly attemptSave = signal(false);
    protected readonly isSaveButtonBusy = computed(() => this.registryService.isSaving());

    protected buttonPermissions: {
        isSimulatable: Signal<boolean>;
        isExportable: Signal<boolean>;
        isCopyable: Signal<boolean>;
        isCloneable: Signal<boolean>;
        isWorkflowable: Signal<boolean>;
        isSnapshot: Signal<boolean>;
        writeActionPermission: Signal<boolean>;
        showDeleteButton: Signal<boolean>;
        showEditButton: Signal<boolean>;
        showArchiveButton: Signal<boolean>;
        showUnarchiveButton: Signal<boolean>;
        showRequestAccessButton: Signal<boolean>;
        showMoveProjectButton: Signal<boolean>;
    } = {
        isSimulatable: computed(() => {
            return SIMULATABLE_OBJECTS.includes(this.object());
        }),
        isExportable: computed(() => {
            return !this.registryService.isEditable() && EXPORTABLE_OBJECTS.includes(this.object());
        }),
        isCopyable: computed(() => {
            return COPYABLE_OBJECTS.includes(this.object());
        }),
        isCloneable: computed(() => {
            return (
                this.buttonPermissions.isWorkflowable() &&
                [WORKFLOW_STATUS.APPROVED, WORKFLOW_STATUS.REJECTED].includes(
                    this.item().currentStatus,
                )
            );
        }),
        isWorkflowable: computed(() => {
            return WORKFLOWABLE_OBJECTS.includes(this.object());
        }),
        isSnapshot: computed(() => {
            return !!this.route.snapshot.params.transactionId;
        }),
        writeActionPermission: computed(() => {
            if (
                // For Org Settings => we need to check permissions
                [OBJECTS.USER, OBJECTS.APPROVAL_WORKFLOW, OBJECTS.PLATFORM_SETTING].includes(
                    this.object(),
                )
            ) {
                return this.authService
                    .currentUser()
                    ?.permissions?.includes(ORGANIZATION_PERMISSION.ORGANIZATION_ADMIN);
            }
            return this.permissionService.canAccessObject(
                Helpers.getPermissionName(this.object(), ACTIONS.WRITE),
            );
        }),
        showDeleteButton: computed(() => {
            if (
                !NON_DELETABLE_OBJECTS.includes(this.object()) &&
                !this.buttonPermissions.isSnapshot()
            ) {
                const hasWriteAccess = this.item()?.actions
                    ? this.item().actions[ACTIONS.WRITE]
                    : false;
                if (this.buttonPermissions.isWorkflowable()) {
                    return (
                        hasWriteAccess &&
                        [WORKFLOW_STATUS.DRAFT, WORKFLOW_STATUS.ARCHIVED].includes(
                            this.item()?.currentStatus,
                        )
                    );
                }
                return hasWriteAccess;
            }

            return false;
        }),
        showEditButton: computed(() => {
            if (
                !this.buttonPermissions.isSnapshot() &&
                // FIXME: We need a better way to handle this instead of hardcoding these here
                !NON_EDITABLE_TABS.includes(this.currentTabPath())
            ) {
                const hasWriteAccess = this.item()?.actions
                    ? this.item().actions[ACTIONS.WRITE]
                    : false;
                if (this.buttonPermissions.isWorkflowable()) {
                    return (
                        hasWriteAccess &&
                        ![WORKFLOW_STATUS.APPROVED, WORKFLOW_STATUS.REJECTED].includes(
                            this.item()?.currentStatus,
                        )
                    );
                }
                return hasWriteAccess;
            }

            return false;
        }),
        showArchiveButton: computed(
            () =>
                this.buttonPermissions.isWorkflowable() &&
                !this.buttonPermissions.isSnapshot() &&
                !this.registryService.isEditable() &&
                this.item()?.actions?.[ACTIONS.WRITE] &&
                this.item()?.currentStatus === WORKFLOW_STATUS.DRAFT,
        ),
        showUnarchiveButton: computed(
            () =>
                this.buttonPermissions.isWorkflowable() &&
                !this.buttonPermissions.isSnapshot() &&
                !this.registryService.isEditable() &&
                this.item()?.actions?.[ACTIONS.WRITE] &&
                this.item()?.currentStatus === WORKFLOW_STATUS.ARCHIVED,
        ),
        showRequestAccessButton: computed(
            () =>
                !!this.currentProject() &&
                !this.buttonPermissions.isSnapshot() &&
                !this.registryService.isEditable() &&
                !!this.item()?.actions?.[ACTIONS.READ] &&
                !this.item()?.actions?.[ACTIONS.WRITE],
        ),
        showMoveProjectButton: computed(
            () =>
                !this.buttonPermissions.isSnapshot() &&
                !this.registryService.isEditable() &&
                this.item()?.actions?.[ACTIONS.WRITE] &&
                !!this.item()?.projectSlug,
        ),
    };

    protected moveProject = {
        visible: signal(false),
        isCopying: signal(false),
        item: signal<Item>(null),
        onMove: (projectSlug: string) => {
            this.moveProject.visible.set(false);
            RouterHelper.switchToObjectProject(this.router, this.currentProject(), projectSlug);
        },
        onCopy: (item: { id: number; projectSlug: string }) => {
            this.moveProject.visible.set(false);
            this.router.navigate([
                RouterHelper.buildRoute({
                    object: this.object(),
                    id: item.id,
                    projectSlug: item.projectSlug,
                }),
            ]);
        },
    };

    private readonly currentTabPath = toSignal(
        this.router.events.pipe(
            filter(event => event instanceof NavigationEnd),
            map(() => this.route.snapshot.firstChild?.routeConfig?.path as TAB_ROUTES),
            startWith(this.route.snapshot.firstChild?.routeConfig?.path as TAB_ROUTES),
        ),
    );

    /**
     * This is used to check if there are any items in the extra options dropdown
     * If there are no items, then extra options button should not be displayed
     */
    protected readonly hasExtraOptions = computed(() => {
        return (
            this.extraOptions() ||
            this.buttonPermissions.isExportable() ||
            this.buttonPermissions.showArchiveButton() ||
            this.buttonPermissions.showUnarchiveButton() ||
            this.buttonPermissions.showDeleteButton()
        );
    });

    protected readonly latestVersionLink = computed(() =>
        RouterHelper.buildRoute({
            object: this.object(), // always required
            id: this.id(), // always required
            projectSlug: this.currentProject(),
        }),
    );

    protected readonly archive = {
        isArchiving: signal(false),
        isUnarchiving: signal(false),
    };

    protected readonly copy = {
        isSaving: signal(false),
    };

    protected readonly copyMenuItems = computed((): MenuItem[] => [
        {
            label: 'Copy to Another Project',
            icon: 'pi pi-box',
            command: () => {
                this.moveProject.isCopying.set(true);
                this.moveProject.visible.set(true);
            },
        },
    ]);

    protected readonly clone = {
        isSaving: signal(false),
    };

    constructor() {
        effect(onCleanup => {
            if (!this.registryService.tabForm()) return;

            this.keyboardShortcutService.listenForSave(
                () =>
                    this.registryService.isEditable() && this.onPreSave(SAVE_TYPES.WITHOUT_COMMENT),
            );
            onCleanup(() => this.keyboardShortcutService.clearHandler());
        });

        effect(() => {
            const activeTab = this.currentTabPath();

            // If the active tab is non-editable, then stop editing
            if (NON_EDITABLE_TABS.includes(activeTab)) {
                this.registryService.isEditable.set(false);
            }
        });
    }

    // Used only for import object
    protected onImport() {
        this.importEvent.emit();
    }

    protected editClick() {
        if (this.item().actions?.Write) {
            this.editEvent.emit();
        } else {
            this.showNoAccessDialog.set(true);
        }
    }

    protected copyItem() {
        if (!this.id() || this.copy.isSaving()) return;

        this.copy.isSaving.set(true);
        this.service()
            .copy(this.id())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response => {
                this.toast.showSuccess('Copy is done successfully');
                this.router.navigate([
                    RouterHelper.buildRoute({
                        object: this.object(),
                        id: response.result.id,
                        projectSlug: this.currentProject(),
                    }),
                ]);
            })
            .add(() => this.copy.isSaving.set(false));
    }

    protected cloneItem() {
        if (!this.id() || this.clone.isSaving() || !this.service().clone) return;

        this.clone.isSaving.set(true);
        this.service()
            .clone(this.id())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(response => {
                this.toast.showSuccess('Clone is done successfully');
                this.router.navigate([
                    RouterHelper.buildRoute({
                        object: this.object(),
                        id: response.result.id,
                        projectSlug: this.currentProject(),
                    }),
                ]);
            })
            .add(() => this.clone.isSaving.set(false));
    }

    // FIXME: this is duplicate logic from change-log component
    protected restoreSnapshotAsCopy() {
        this.service()
            .restoreSnapshotAsCopy(this.id(), this.route.snapshot.params.transactionId)
            .subscribe({
                next: res => {
                    this.toast.showSuccess(`${'Successfully copied the snapshot'}`);

                    this.router.navigate([
                        `${RouterHelper.buildRoute({
                            object: this.object(),
                            id: res.result.id,
                            projectSlug: res.result.projectSlug,
                        })}`,
                    ]);
                },
                error: () => {
                    this.toast.showError(`${'Failed to restore the snapshot'}`);
                },
            });
    }

    // FIXME: this is duplicate logic from change-log component
    protected restoreSnapshot() {
        this.service()
            .restoreSnapshot(this.id(), this.route.snapshot.params.transactionId)
            .subscribe({
                next: () => {
                    this.router.navigate([this.latestVersionLink()]);
                    this.toast.showSuccess(`${'Successfully restored the snapshot'}`);
                },
                error: () => {
                    this.toast.showError(`${'Failed to restore the snapshot'}`);
                },
            });
    }

    protected archiveItem() {
        this.confirmationService.confirm({
            key: 'confirm-dialog',
            header: 'Archive',
            message:
                'Archive hides this draft from the default catalog view. You can restore it later with "Unarchive".',
            closeOnEscape: true,
            acceptLabel: 'Archive',
            rejectLabel: 'Cancel',
            rejectButtonProps: {
                severity: 'secondary',
                outlined: true,
                size: 'small',
                styleClass: 'rounded',
            },
            acceptButtonProps: {
                severity: 'primary',
                size: 'small',
                styleClass: 'rounded',
            },
            accept: () => {
                if (!this.id() || this.archive.isArchiving()) return;
                this.archive.isArchiving.set(true);
                this.service()
                    .updateStatus(this.id(), { status: WORKFLOW_STATUS.ARCHIVED })
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe(() => {
                        this.toast.showSuccess('Archived successfully');
                        // Reload because we don't have good state mgmt for tabs component > status tag
                        RouterHelper.reloadPage(this.router, this.route);
                    })
                    .add(() => this.archive.isArchiving.set(false));
            },
        });
    }

    protected unarchiveItem() {
        if (!this.id() || this.archive.isUnarchiving()) return;

        this.archive.isUnarchiving.set(true);
        this.service()
            .updateStatus(this.id(), { status: WORKFLOW_STATUS.DRAFT })
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.toast.showSuccess('Restored to Draft');
                // Reload because we don't have good state mgmt for tabs component > status tag
                RouterHelper.reloadPage(this.router, this.route);
            })
            .add(() => this.archive.isUnarchiving.set(false));
    }

    protected onCancelClick(): void {
        this.stopEditingEvent.emit();
    }

    protected onPreSave(saveType: SAVE_TYPES): void {
        const form = this.registryService.tabForm();
        if (!form) return;

        if (form.valid) {
            if (saveType === SAVE_TYPES.WITH_COMMENT) {
                this.attemptSave.set(true);
            } else {
                this.onSaveConfirmation();
            }
        } else {
            FormHelper.showFormErrors(form as FormGroup, this.toast);
        }
    }

    protected onSaveConfirmation(snapshotInfo?: { versionName: string; comment: string }): void {
        this.onSaveClick({
            versionName: snapshotInfo?.versionName,
            comment: snapshotInfo?.comment,
        });
    }

    protected onSaveClick(payload: { versionName: string; comment: string }): void {
        // NOTE: We store which object is being saved in the saveRequest
        this.registryService.saveRequested$.next({ object: this.object(), ...payload });
    }
}
